import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@kopi/ui/components/card";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ChevronRightIcon,
  PaletteIcon,
  PlusIcon,
  QrCodeIcon,
  ReceiptTextIcon,
  ShieldCheckIcon,
  StoreIcon,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { AddOutletDialog } from "@/components/add-outlet";
import { TenantLogo, useTenantBrand } from "@/components/brand";
import { useCurrentOutlet } from "@/components/current-outlet";
import { Page, PageHeader } from "@/components/page-header";
import { settings } from "@/i18n/messages/settings";
import { useMessages } from "@/i18n/provider";
import { canAnywhere, canAt, canTenantWide, hasRole } from "@/lib/access";
import { meScopesQuery } from "@/lib/queries";

export const Route = createFileRoute("/_authed/settings/")({
  staticData: { section: "preferences", crumb: "nav.settings" },
  component: SettingsPage,
});

const ROW =
  "flex min-h-14 items-center gap-3 rounded-lg px-3 py-2 transition-colors duration-150 hover:bg-muted motion-reduce:transition-none";

function SettingsPage() {
  const { t } = useMessages(settings);
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const { outlets } = useCurrentOutlet();
  const brand = useTenantBrand();
  const editableOutlets = outlets.filter((o) => canAt(scopes, "outlets.settings", o.id));
  // Adding outlets is an owner action (`outlets.manage` tenant-wide), like deactivating them.
  const canAddOutlet = canTenantWide(scopes, "outlets.manage");
  const [adding, setAdding] = useState(false);

  return (
    <Page width="narrow">
      <PageHeader title={t("nav.settings")} subtitle={t("settings.subtitle")} />
      {canTenantWide(scopes, "outlets.manage") && (
        <SettingsCard icon={<PaletteIcon />} title={t("page.brand")} description={t("settings.brandHint")}>
          <Link to="/settings/brand" className={ROW}>
            <TenantLogo brand={brand} variant="mark" size={40} />
            <span className="min-w-0 flex-1 truncate font-medium">{brand.name}</span>
            {brand.isDefaultName && <Badge variant="outline">{t("settings.default")}</Badge>}
            <ChevronRightIcon className="size-4 text-muted-foreground" />
          </Link>
        </SettingsCard>
      )}
      {(editableOutlets.length > 0 || canAddOutlet) && (
        <SettingsCard
          icon={<StoreIcon />}
          title={t("outlet.heading")}
          description={t("settings.outletsHint")}
        >
          {/* Not virtualised (CODING_STANDARDS §4 exception): a tenant's outlets are a small, bounded set. */}
          <ul className="flex flex-col">
            {editableOutlets.map((outlet) => (
              <li key={outlet.id}>
                <Link to="/outlets/$outletId" params={{ outletId: outlet.id }} className={ROW}>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate font-medium">{outlet.name}</span>
                    <span className="text-muted-foreground text-sm">
                      {outlet.code} · {outlet.timezone}
                    </span>
                  </span>
                  {!outlet.active && <Badge variant="outline">{t("common.inactive")}</Badge>}
                  <ChevronRightIcon className="size-4 text-muted-foreground" />
                </Link>
              </li>
            ))}
          </ul>
          {canAddOutlet && (
            <Button type="button" variant="outline" className="mt-2 ml-3" onClick={() => setAdding(true)}>
              <PlusIcon />
              {t("settings.addOutlet")}
            </Button>
          )}
        </SettingsCard>
      )}
      {canAnywhere(scopes, "tax.manage") && (
        <SettingsCard icon={<ReceiptTextIcon />} title={t("page.tax")} description={t("settings.taxHint")}>
          <Link to="/settings/tax" className={ROW}>
            <span className="flex-1 font-medium">{t("settings.taxRules")}</span>
            <ChevronRightIcon className="size-4 text-muted-foreground" />
          </Link>
        </SettingsCard>
      )}
      {canAnywhere(scopes, "settings.payment") && (
        <SettingsCard
          icon={<QrCodeIcon />}
          title={t("page.payments")}
          description={t("settings.paymentsHint")}
        >
          <Link to="/settings/payments" className={ROW}>
            <span className="flex-1 font-medium">{t("settings.paymentMethods")}</span>
            <ChevronRightIcon className="size-4 text-muted-foreground" />
          </Link>
        </SettingsCard>
      )}
      {hasRole(scopes, "owner") && (
        <SettingsCard
          icon={<ShieldCheckIcon />}
          title={t("page.security")}
          description={t("settings.securityHint")}
        >
          <Link to="/security" className={ROW}>
            <span className="flex-1 font-medium">{t("settings.twoStep")}</span>
            <ChevronRightIcon className="size-4 text-muted-foreground" />
          </Link>
        </SettingsCard>
      )}
      {adding && <AddOutletDialog onClose={() => setAdding(false)} />}
    </Page>
  );
}

function SettingsCard(props: { icon: ReactNode; title: string; description: string; children: ReactNode }) {
  return (
    <Card className="gap-4">
      <CardHeader className="grid-cols-[auto_1fr] items-center gap-x-3">
        <span className="row-span-2 inline-flex size-10 items-center justify-center rounded-lg bg-muted text-primary [&_svg]:size-5">
          {props.icon}
        </span>
        <CardTitle>{props.title}</CardTitle>
        <CardDescription>{props.description}</CardDescription>
      </CardHeader>
      <CardContent className="px-3">{props.children}</CardContent>
    </Card>
  );
}
